// This module owns browser-native keyboard, mouse, and touch input for Keys.
// It translates DOM events into already-resolved hex coords and calls the live
// note lifecycle methods on Keys. It does not derive MIDI/controller mappings
// or harmonic frames.

import Point from "./point";
import { withTouchTiming, markInputHandler } from "../audio/runtime-diagnostics.js";
import { getTouchInputMode, setTouchInputMode } from "./touch-input-mode.js";
import { recordAudioRuntime } from "../audio/runtime-diagnostics.js";

// Windows benefits from Pointer Events plus pointer capture: preventDefault and
// touch-action:none keep the OS from interpreting multi-touch canvas gestures
// as scrolling. iOS's established Touch Events path has more reliable tap/latch
// semantics across Safari-based browsers, so retain it there.
export function shouldUsePointerTouchInput(windowObject, navigatorObject, mode = "auto") {
  if (typeof windowObject?.PointerEvent !== "function") return false;
  if (mode !== "auto") return mode === "pointer";
  const platform = String(navigatorObject?.platform ?? "");
  const userAgent = String(navigatorObject?.userAgent ?? "");
  const isAppleTouchDevice =
    /iPhone|iPad|iPod/i.test(userAgent) ||
    (platform === "MacIntel" && Number(navigatorObject?.maxTouchPoints) > 1);
  return !isAppleTouchDevice;
}

export function removeTouchInputListeners(keys) {
  const canvas = keys.state.canvas;
  for (const [type, handler] of [
    ["pointerdown", keys.handlePointerDown], ["pointermove", keys.handlePointerMove],
    ["pointerup", keys.handlePointerEnd], ["pointercancel", keys.handlePointerEnd],
    ["lostpointercapture", keys.handlePointerEnd], ["touchstart", keys.handleTouch],
    ["touchmove", keys.handleTouch], ["touchend", keys.handleTouch],
    ["touchcancel", keys.handleTouchCancel],
  ]) canvas.removeEventListener(type, handler, false);
}

export function configureTouchInput(mode = getTouchInputMode()) {
  this.releaseActiveBrowserNotes?.();
  removeTouchInputListeners(this);
  setTouchInputMode(mode);
  this.usesPointerTouchInput = shouldUsePointerTouchInput(window, navigator, mode);
  this._pointerMouseInput = false;
  const canvas = this.state.canvas;
  // Set before the next contact starts; changing this during a gesture is too late.
  canvas.style.touchAction = "none";
  const listeners = this.usesPointerTouchInput
    ? [["pointerdown", this.handlePointerDown], ["pointermove", this.handlePointerMove],
      ["pointerup", this.handlePointerEnd], ["pointercancel", this.handlePointerEnd],
      ["lostpointercapture", this.handlePointerEnd]]
    : [["touchstart", this.handleTouch], ["touchmove", this.handleTouch],
      ["touchend", this.handleTouch], ["touchcancel", this.handleTouchCancel]];
  for (const [type, handler] of listeners) canvas.addEventListener(type, handler, { passive: false });
  recordAudioRuntime("touch-input-mode", { requested: mode,
    active: this.usesPointerTouchInput ? "pointer" : "touch" });
}

function isModulationToggleKeyCode(code) {
  return code === "Backquote" || code === "IntlBackslash";
}

function isPanicKeyEvent(e) {
  if (e.repeat) return false;
  if (e.shiftKey && !e.metaKey && !e.ctrlKey && !e.altKey && e.code === "Backspace") return true;
  return e.metaKey && e.key === "Delete";
}

function isShiftOnlyShortcut(e) {
  return e.shiftKey && !e.metaKey && !e.ctrlKey && !e.altKey;
}

function isSustainToggleShortcut(e) {
  if (e.code === "Escape") return isShiftOnlyShortcut(e);
  return e.code === "F8" && !e.shiftKey && !e.metaKey && !e.ctrlKey && !e.altKey;
}

function keyboardCoordsForCode(keys, code) {
  const kbOffset = keys.settings.centerHexOffset;
  const kbRaw = keys.settings.keyCodeToCoords[code];
  return new Point(kbRaw.x + kbOffset.x, kbRaw.y + kbOffset.y);
}

function canMomentarilyFlipHakenGlide(keys) {
  if (keys.inputIsFocused()) return false;
  if (keys.controller?.id !== "hakenaudio") return false;
  if (!keys.inputRuntime?.mpeInput) return false;
  const mode = keys.inputRuntime.hakenXGlideMode ?? "pitch_bending";
  return mode === "raster_to_notes" || mode === "pitch_bending";
}

function findSustainedIndexAt(state, coords) {
  return state.sustainedNotes.findIndex(
    ([h]) => h.coords.x === coords.x && h.coords.y === coords.y,
  );
}

function isShiftLatchedAt(keys, coords) {
  const hexIndex = findSustainedIndexAt(keys.state, coords);
  if (hexIndex === -1) return false;
  const [hex] = keys.state.sustainedNotes[hexIndex];
  for (const [code, activeHex] of keys.state.activeKeyboard) {
    if (activeHex === hex && keys.state.shiftSustainedKeys.has(code)) return true;
  }
  return false;
}

function releaseSustainedAt(keys, coords) {
  if (keys.state.latch && keys._latchToggleAtCoord?.(coords)) return true;
  const hexIndex = findSustainedIndexAt(keys.state, coords);
  if (hexIndex === -1) return false;
  const [hex, vel] = keys.state.sustainedNotes[hexIndex];
  keys.state.sustainedNotes.splice(hexIndex, 1);
  keys.state.sustainedCoords.delete(`${coords.x},${coords.y}`);

  // A note latched with Shift remains in activeKeyboard after physical key-up
  // so it can still participate in retuning and snapshots. If another input
  // source toggles that sustained note off, release this stale ownership too;
  // otherwise the canvas and PANIC continue to treat the released Hex as held.
  for (const [code, activeHex] of keys.state.activeKeyboard) {
    if (activeHex !== hex) continue;
    keys.state.activeKeyboard.delete(code);
    keys.state.shiftSustainedKeys.delete(code);
    keys.state.pressedKeys.delete(code);
  }

  hex.noteOff(vel);
  keys._trackRecentlyReleasedHex?.(hex);
  keys.recencyStack?.remove?.(hex);
  keys._updateWheelTarget?.(true);
  keys._syncAwaitingModulationSource?.();
  keys._scheduleDeferredBulkRefresh?.();
  keys._settleModulationAfterActiveRelease?.();
  keys.hexOff(coords);
  keys._emitLiveNoteDisplayState?.();
  return true;
}

function toggleLatchedAt(keys, coords) {
  if (!keys.state.latch) return false;
  return keys._latchToggleAtCoord
    ? keys._latchToggleAtCoord(coords)
    : releaseSustainedAt(keys, coords);
}

export function inputIsFocused() {
  const tag = document.activeElement && document.activeElement.tagName;
  return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT";
}

export function setTuneDragging(active) {
  this.state.isTuneDragging = active;
}

export function onKeyDown(e) {
  if (
    e.code === "Space" &&
    !e.repeat &&
    !e.metaKey &&
    !e.ctrlKey &&
    !e.altKey &&
    canMomentarilyFlipHakenGlide(this)
  ) {
    e.preventDefault();
    this._setHakenSpaceGlideFlip?.(true);
    return;
  }

  if (this.inputIsFocused()) return;

  if (isPanicKeyEvent(e)) {
    e.preventDefault?.();
    this.panic();
    return;
  }

  if (!e.repeat && isSustainToggleShortcut(e)) {
    e.preventDefault?.();
    this.state.escHeld = true;
    this.latchToggle();
    return;
  }

  if (e.code === "Enter" && !e.repeat && isShiftOnlyShortcut(e)) {
    const hasNotes =
      this.state.activeMouse !== null ||
      this.state.activeTouch.size > 0 ||
      this.state.activeKeyboard.size > 0 ||
      this.state.activeMidi.size > 0 ||
      this.state.sustainedNotes.length > 0;
    if (hasNotes && this.onTakeSnapshot) {
      e.preventDefault?.();
      this.onTakeSnapshot();
      return;
    }
  }

  if (isModulationToggleKeyCode(e.code) && !e.repeat && isShiftOnlyShortcut(e)) {
    e.preventDefault();
    this.toggleModulationArm();
    return;
  }

  if (!this.typing) return;
  if (e.metaKey || e.ctrlKey || e.altKey) return;

  e.preventDefault();
  if (e.repeat) return;
  if (!this.settings?.keyCodeToCoords) return;

  if (!(e.code in this.settings.keyCodeToCoords)) return;

  if (e.shiftKey) {
    const coords = keyboardCoordsForCode(this, e.code);
    if (this.state.shiftSustainedKeys.has(e.code)) {
      this.state.shiftSustainedKeys.delete(e.code);
      releaseSustainedAt(this, coords);
      this.state.activeKeyboard.delete(e.code);
      return;
    }

    this.state.pressedKeys.add(e.code);
    this.state.shiftSustainedKeys.add(e.code);
    const hex = this.hexOn(coords);
    this.state.activeKeyboard.set(e.code, hex);
    this.state.sustainedNotes.push([hex, 0]);
    this.state.sustainedCoords.add(`${coords.x},${coords.y}`);
    return;
  }

  if (this.state.shiftSustainedKeys.has(e.code)) {
    this.state.shiftSustainedKeys.delete(e.code);
    const coords = keyboardCoordsForCode(this, e.code);
    releaseSustainedAt(this, coords);
    this.state.activeKeyboard.delete(e.code);
    return;
  }

  if (this.state.pressedKeys.has(e.code)) return;

  const coords = keyboardCoordsForCode(this, e.code);
  if (toggleLatchedAt(this, coords)) return;

  this.state.pressedKeys.add(e.code);
  const hex = this.hexOn(coords);
  this.state.activeKeyboard.set(e.code, hex);
}

export function onKeyUp(e) {
  if (
    e.code === "Space" &&
    !e.metaKey &&
    !e.ctrlKey &&
    !e.altKey &&
    canMomentarilyFlipHakenGlide(this)
  ) {
    e.preventDefault();
    this._setHakenSpaceGlideFlip?.(false);
    return;
  }

  if ((e.code === "Escape" || e.code === "F8") && this.state.escHeld) {
    this.state.escHeld = false;
    return;
  }

  if (isModulationToggleKeyCode(e.code)) return;
  if (!this.typing) return;
  if (this.inputIsFocused()) return;

  if (!(e.code in this.settings.keyCodeToCoords)) return;
  if (this.state.shiftSustainedKeys.has(e.code)) {
    this.state.pressedKeys.delete(e.code);
    return;
  }
  if (!this.state.pressedKeys.has(e.code)) return;

  this.state.pressedKeys.delete(e.code);
  const coords = keyboardCoordsForCode(this, e.code);
  const hex = this.state.activeKeyboard.get(e.code);
  if (hex) {
    this.noteOff(hex, 0);
    this.state.activeKeyboard.delete(e.code);
    this._settleModulationAfterActiveRelease();
  }
  if (!this.state.sustain) this.hexOff(coords);
}

export function mouseUp(_e) {
  if (_e?.sourceCapabilities?.firesTouchEvents) return;
  if (!this.state.isMouseDown) return;
  this.state.isMouseDown = false;
  this.state.mouseDownToggledCoord = null;
  this.state.canvas.removeEventListener("mousemove", this.mouseActive);

  if (this.state.activeMouse) {
    const coords = this.state.activeMouse.coords;
    this.noteOff(this.state.activeMouse, 0);
    this.state.activeMouse = null;
    this._settleModulationAfterActiveRelease();
    if (!this.state.sustain) this.hexOff(coords);
  }

  if (!this.state.escHeld && this.state.sustain && !this.state.isTuneDragging) {
    this.sustainOff();
  }
}

export async function mouseDown(e) {
  if (e.sourceCapabilities?.firesTouchEvents) return;
  // In Pointer Events mode only a preceding real mouse pointerdown may
  // enter this legacy path. Compatibility mouse events after a touch lift
  // need not expose sourceCapabilities.
  if (this.usesPointerTouchInput && !this._pointerMouseInput) return;
  if (this._onFirstInteraction) {
    // Do not block visual/touch responsiveness on audio wake/prepare work.
    // On iOS restore paths that async preparation can stall long enough to
    // swallow the first gesture entirely if we await it here.
    void this._onFirstInteraction();
  }

  if (this.state.activeMouse) {
    this.state.activeMouse.noteOff(0);
    this.state.activeMouse = null;
  }

  this.state.mouseDownToggledCoord = null;
  this.state.isMouseDown = true;
  this.state.canvas.addEventListener("mousemove", this.mouseActive, false);
  this.mouseActive(e);
}

export function mouseActive(e) {
  if (e.sourceCapabilities?.firesTouchEvents) return;
  if (this.usesPointerTouchInput && !this._pointerMouseInput) return;
  let coords = this.getPointerPosition(e);
  coords = this.getHexCoordsAt(coords);

  if (this.state.activeMouse === null) {
    const key = `${coords.x},${coords.y}`;
    // Shift-latched notes are sustained independently of the global latch.
    // Playing the same canvas key must therefore toggle either kind off.
    if (
      toggleLatchedAt(this, coords) ||
      (!this.state.latch && isShiftLatchedAt(this, coords) && releaseSustainedAt(this, coords))
    ) {
      this.state.mouseDownToggledCoord = key;
      return;
    }
    if (this.state.mouseDownToggledCoord === key) return;
    this.state.activeMouse = this.hexOn(coords);
    return;
  }

  const first = this.state.activeMouse;
  if (coords.equals(first.coords)) return;

  if (this.state.latch) {
    const key = `${coords.x},${coords.y}`;
    if (toggleLatchedAt(this, coords)) {
      const oldCoords = first.coords;
      this.noteOff(first, 0);
      this.state.activeMouse = null;
      this._settleModulationAfterActiveRelease();
      this.hexOff(oldCoords);
      this.state.mouseDownToggledCoord = key;
      return;
    }
  }

  const oldCoords = first.coords;
  this.noteOff(first, 0);
  this.state.activeMouse = null;
  this._settleModulationAfterActiveRelease();
  this.hexOff(oldCoords);
  this.state.activeMouse = this.hexOn(coords);
}

export function getPointerPosition(e) {
  const rect = e.currentTarget.getBoundingClientRect();
  return new Point(e.clientX - rect.left, e.clientY - rect.top);
}

export function getPosition(element) {
  const rect = element.getBoundingClientRect();
  return { x: rect.left, y: rect.top };
}

function releaseTouch(keys, id) {
  const hex = keys.state.activeTouch.get(id);
  keys.state.activeTouch.delete(id);
  keys.state.touchCoords.delete(id);
  if (!hex) return;
  keys.noteOff(hex, 0);
  keys._settleModulationAfterActiveRelease();
  if (!keys.state.sustain) keys.hexOff(hex.coords);
}

function releaseCapturedPointer(canvas, pointerId) {
  try {
    if (canvas.hasPointerCapture?.(pointerId)) canvas.releasePointerCapture(pointerId);
  } catch {
    // Pointer capture may already have been released by the browser.
  }
}

export function handlePointerDown(e) {
  markInputHandler(e);
  this._pointerMouseInput = e.pointerType === "mouse";
  if (e.pointerType === "mouse") return;
  e.preventDefault?.();
  if (this._onFirstInteraction) void this._onFirstInteraction();

  const id = e.pointerId;
  // A reused pointer id should never leave an earlier voice owned.
  releaseTouch(this, id);
  const coords = this.getHexCoordsAt(this.getPointerPosition(e));
  this.state.touchCoords.set(id, coords);
  this.state.isTouchDown = true;
  withTouchTiming(e, () => this._touchStartOnCoords(id, coords));

  try {
    e.currentTarget.setPointerCapture?.(id);
  } catch {
    // Some synthetic/older pointer implementations do not support capture.
  }
}

export function handlePointerMove(e) {
  markInputHandler(e);
  if (e.pointerType === "mouse" || !this.state.touchCoords.has(e.pointerId)) return;
  // A final lift/hover move must not glide into a new key and attack it.
  if (e.buttons === 0) {
    handlePointerEnd.call(this, e);
    return;
  }
  e.preventDefault?.();
  const id = e.pointerId;
  const coords = this.getHexCoordsAt(this.getPointerPosition(e));
  if (this.state.touchCoords.get(id)?.equals(coords)) return;
  releaseTouch(this, id);
  this.state.touchCoords.set(id, coords);
  withTouchTiming(e, () => this._touchStartOnCoords(id, coords));
}

export function handlePointerEnd(e) {
  if (e.pointerType === "mouse") return;
  e.preventDefault?.();
  releaseTouch(this, e.pointerId);
  releaseCapturedPointer(e.currentTarget, e.pointerId);
  this.state.isTouchDown = this.state.touchCoords.size > 0;
}

export function releaseActiveBrowserNotes() {
  const canvas = this.state.canvas;
  for (const pointerId of [...this.state.touchCoords.keys()]) {
    releaseTouch(this, pointerId);
    releaseCapturedPointer(canvas, pointerId);
  }
  this.state.isTouchDown = false;

  if (this.state.activeMouse) {
    const hex = this.state.activeMouse;
    this.state.activeMouse = null;
    this.noteOff(hex, 0);
    if (!this.state.sustain) this.hexOff(hex.coords);
    this._settleModulationAfterActiveRelease();
  }
  this.state.isMouseDown = false;
  canvas?.removeEventListener("mousemove", this.mouseActive);
  this.releaseAllKeyboardNotes?.();
}

export async function handleTouch(e) {
  markInputHandler(e);
  e.preventDefault();
  if (this._onFirstInteraction) {
    // Audio preparation must not delay the gesture's note lifecycle.
    void this._onFirstInteraction();
  }

  this.state.isTouchDown = e.targetTouches.length !== 0;
  const currentIds = new Set(Array.from(e.targetTouches, (touch) => touch.identifier));
  for (const id of this.state.touchCoords.keys()) {
    if (!currentIds.has(id)) releaseTouch(this, id);
  }

  const rect = this.state.canvas.getBoundingClientRect();
  for (const touch of e.targetTouches) {
    const id = touch.identifier;
    const coords = this.getHexCoordsAt(
      new Point(touch.clientX - rect.left, touch.clientY - rect.top),
    );
    // Track the contact even when it toggled a latched note off or its note-on
    // was suppressed. Stationary movement and other fingers' events must not
    // turn that same key back on.
    if (this.state.touchCoords.get(id)?.equals(coords)) continue;
    releaseTouch(this, id);
    this.state.touchCoords.set(id, coords);
    withTouchTiming(e, () => this._touchStartOnCoords(id, coords));
  }
}

export function touchStartOnCoords(id, coords) {
  if (
    toggleLatchedAt(this, coords) ||
    (!this.state.latch && isShiftLatchedAt(this, coords) && releaseSustainedAt(this, coords))
  ) {
    return;
  }
  const newHex = this.hexOn(coords);
  if (newHex) this.state.activeTouch.set(id, newHex);
}

export function handleTouchCancel(e) {
  // A browser may cancel just one contact while the other fingers remain down.
  const ids = e?.changedTouches
    ? Array.from(e.changedTouches, (touch) => touch.identifier)
    : [...this.state.touchCoords.keys()];
  for (const id of ids) releaseTouch(this, id);
  this.state.isTouchDown = this.state.touchCoords.size > 0;
}
